/**
 * Apply a mapped CSV/Excel item import with citizenship semantics:
 *   - Match: id → external_id → insert (Import API order)
 *   - Persist items.external_id + entity_external_ids (connector csv)
 *   - Log runs via change_events; clarifications as conflicted rows (Conflict Queue)
 *
 * Entry: ItemImportExportModal.importMappedItems (list toolbar).
 */

import { getSupabaseClient } from '../supabaseClient';
import { getCurrentWorkspaceId } from '../supabaseSync';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { logChangeEvent, registerExternalId } from '../connectors';
import type { Item, ItemLanguageType, ItemMetaCategory, StrategicBucket } from '../../types/prioritization/lifecycle';
import type { AltitudeLevel } from '../../types/prioritization/itemLifecycle';
import type { Candidate } from '../../types/prioritization/lifecycle';
import {
  classifyCsvDeltaRows,
  snapshotComparableFields,
  summarizeCsvDelta,
  type CsvDeltaExistingItem,
  type CsvDeltaRowResult,
} from './csvDeltaEngine';
import { CSV_CONNECTOR_TYPE } from './csvMappingProfiles';
import { buildPriorImportIndex, describeSkippedAsImported, newImportBatchId, wasAlreadyImported } from '../importBatch';

export interface CsvCitizenshipImportResult {
  created: number;
  updated: number;
  unchanged: number;
  clarified: number;
  failed: number;
  /**
   * One entry per row that did not land, with the reason the store gave.
   * `rowNumber` is 1-based in the order the rows were supplied.
   */
  failures: CsvImportRowFailure[];
  /** Ids of items this run created, so the caller can point at them. */
  createdItemIds: string[];
  /** Rows not created because an earlier, un-retired import already did. */
  skippedAsImported: number;
  /** The batch id stamped on every item this run created (items.import_batch_id). */
  importBatchId: string;
  summaryLabel: string;
}

export interface CsvImportRowFailure {
  rowNumber: number;
  title: string;
  reason: string;
}

function itemToExisting(item: Item): CsvDeltaExistingItem {
  const tags = Array.isArray(item.tags) ? item.tags : [];
  const fields = snapshotComparableFields({
    title: item.title,
    description: item.description,
    itemCategory: item.itemCategory,
    metaCategory: item.metaCategory,
    strategicBucket: item.strategicBucket,
    altitudeLevel: item.altitudeLevel,
    source: item.source,
    tags,
    desiredOutcome: item.desiredOutcome,
    beneficiary: item.beneficiary,
    evidenceOfSuccess: item.evidenceOfSuccess,
    whyNow: item.whyNow,
    obstacles: item.obstacles,
    work_type: item.work_type,
    launch_type: item.launch_type,
    investment_type: item.investment_type,
    exit_status: item.exit_status,
    parentItemId: item.parentItemId,
  });
  return {
    id: item.id,
    external_id: item.external_id ?? (item as { externalId?: string | null }).externalId ?? null,
    fields,
  };
}

function parseTags(row: Record<string, unknown>): string[] {
  if (Array.isArray(row.tags)) return row.tags.map(String);
  return String(row.tags ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

function rowToItemPatch(row: Record<string, unknown>): Partial<Item> {
  const tags = parseTags(row);
  const patch: Partial<Item> = {};
  if ('title' in row && String(row.title ?? '').trim()) patch.title = String(row.title).trim();
  if ('description' in row) patch.description = String(row.description ?? '');
  if (tags.length || 'tags' in row) patch.tags = tags;
  if (row.source != null && String(row.source).trim()) patch.source = String(row.source);
  if (row.itemCategory) patch.itemCategory = String(row.itemCategory) as ItemLanguageType;
  if (row.metaCategory) patch.metaCategory = String(row.metaCategory) as ItemMetaCategory;
  if (row.strategicBucket) patch.strategicBucket = String(row.strategicBucket) as StrategicBucket;
  if (row.altitudeLevel) {
    // AltitudeLevel is the numeric union 1-5, not a string — the previous
    // `String(row.altitudeLevel) as AltitudeLevel` cast silently produced a
    // stringified value ("3") typed as the number 3, which would fail any
    // strict numeric comparison downstream. Parse and validate instead.
    const parsedAltitude = Number(row.altitudeLevel);
    if (Number.isInteger(parsedAltitude) && parsedAltitude >= 1 && parsedAltitude <= 5) {
      patch.altitudeLevel = parsedAltitude as AltitudeLevel;
    }
  }
  if (row.desiredOutcome) patch.desiredOutcome = String(row.desiredOutcome);
  if (row.beneficiary) patch.beneficiary = String(row.beneficiary);
  if (row.evidenceOfSuccess) patch.evidenceOfSuccess = String(row.evidenceOfSuccess);
  if (row.whyNow) patch.whyNow = String(row.whyNow);
  if (row.obstacles) patch.obstacles = String(row.obstacles);
  if (row.work_type) patch.work_type = String(row.work_type);
  if (row.launch_type) patch.launch_type = String(row.launch_type);
  if (row.investment_type) patch.investment_type = String(row.investment_type);
  if (row.exit_status) patch.exit_status = String(row.exit_status) as Candidate['exit_status'];
  if (row.parentItemId) patch.parentItemId = String(row.parentItemId);
  if (row.external_id != null && String(row.external_id).trim()) {
    patch.external_id = String(row.external_id).trim();
  }
  if (row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)) {
    patch.metadata = row.metadata as Record<string, unknown>;
  }
  return patch;
}

function newUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`;
}

async function logCsvClarification(params: {
  workspaceId: string;
  row: CsvDeltaRowResult;
}): Promise<void> {
  try {
    const supabase = getSupabaseClient();
    const entityId = params.row.matchItemId ?? newUuid();
    const runStamp = Date.now();
    await supabase.from('change_events').insert({
      workspace_id: params.workspaceId,
      source: CSV_CONNECTOR_TYPE,
      entity_type: 'item',
      entity_id: entityId,
      field_name: params.row.clarifyReason ?? 'csv_clarify',
      old_value: null,
      new_value: {
        from: 'csv_import',
        rowIndex: params.row.rowIndex,
        externalId: params.row.externalId ?? null,
        reason: params.row.clarifyReason,
        title: params.row.row.title ?? null,
      },
      event_type: 'conflict_detected',
      sync_status: 'conflicted',
      conflict_data: {
        fieldName: params.row.clarifyReason ?? 'csv_clarify',
        localValue: null,
        incomingValue: params.row.row,
        detectedAt: new Date().toISOString(),
        kind: 'csv_clarification',
      },
      external_id: `csv-clarify:${params.row.externalId ?? 'row'}:${params.row.rowIndex}:${runStamp}`,
    });
  } catch (err) {
    console.warn('[csvCitizenshipImport] clarification log failed:', err);
  }
}

async function logCsvRunSummary(params: {
  workspaceId: string;
  summary: ReturnType<typeof summarizeCsvDelta>;
  profileName?: string;
  anchorEntityId?: string;
}): Promise<void> {
  if (!params.anchorEntityId) return;
  await logChangeEvent({
    workspaceId: params.workspaceId,
    entityType: 'item',
    entityId: params.anchorEntityId,
    eventType: 'synced',
    fieldName: 'csv_import_run',
    newValue: {
      from: 'csv_import',
      profileName: params.profileName ?? null,
      ...params.summary,
      at: new Date().toISOString(),
    },
  });
}

/**
 * Apply mapped rows with upsert-by-external_id (and id) semantics.
 */
export async function applyCsvCitizenshipImport(
  rows: Record<string, unknown>[],
  options?: {
    profileName?: string;
    defaults?: Record<string, string>;
    externalKeyField?: string;
    /** One id per import run; minted here when the caller doesn't supply one. */
    importBatchId?: string;
  },
): Promise<CsvCitizenshipImportResult> {
  const workspaceId = getCurrentWorkspaceId();
  const store = useItemsStore.getState();
  const importBatchId = options?.importBatchId ?? newImportBatchId();
  const existing = store.items.map(itemToExisting);
  // Rows with an external_id already match existing items (update/unchanged)
  // in classifyCsvDeltaRows. Rows WITHOUT one would be created again on every
  // re-run; skip those whose title + source already landed in an earlier,
  // un-retired import batch.
  const priorImports = buildPriorImportIndex(store.items);
  let skippedAsImported = 0;
  const withDefaults = options?.defaults
    ? rows.map((r) => ({ ...options.defaults, ...r }))
    : rows;
  const classified = classifyCsvDeltaRows(withDefaults, existing, {
    externalKeyField: options?.externalKeyField,
  });
  const summary = summarizeCsvDelta(classified);

  let created = 0;
  let updated = 0;
  let unchanged = 0;
  let clarified = 0;
  let failed = 0;
  const failures: CsvImportRowFailure[] = [];
  const createdItemIds: string[] = [];
  let anchorEntityId: string | undefined;
  const identityRecords: Array<{ internalId: string; externalId: string; label?: string }> = [];

  for (const [rowIndex, result] of classified.entries()) {
    const rowNumber = rowIndex + 1;
    if (result.disposition === 'clarify') {
      clarified += 1;
      if (workspaceId) await logCsvClarification({ workspaceId, row: result });
      continue;
    }

    if (result.disposition === 'missing' || result.disposition === 'skip') {
      continue;
    }

    if (result.disposition === 'unchanged') {
      unchanged += 1;
      if (result.externalId && result.matchItemId) {
        identityRecords.push({
          internalId: result.matchItemId,
          externalId: result.externalId,
          label: String(result.row.title ?? ''),
        });
      }
      continue;
    }

    if (result.disposition === 'create') {
      const patch = rowToItemPatch(result.row);
      const title = String(patch.title ?? '').trim();
      if (!title) {
        failed += 1;
        failures.push({ rowNumber, title: '(no title)', reason: 'Row has no title' });
        continue;
      }
      const source = patch.source ?? 'Excel/CSV import';
      if (!patch.external_id && wasAlreadyImported(priorImports, { title, source })) {
        skippedAsImported += 1;
        continue;
      }
      const newId = newUuid();
      const createResult = await store.createItemWithReason({
        id: newId,
        importBatchId,
        title,
        description: patch.description ?? '',
        tags: patch.tags ?? [],
        source,
        ...(patch.itemCategory && { itemCategory: patch.itemCategory }),
        ...(patch.metaCategory && { metaCategory: patch.metaCategory }),
        ...(patch.strategicBucket && { strategicBucket: patch.strategicBucket }),
        ...(patch.altitudeLevel && { altitudeLevel: patch.altitudeLevel }),
        ...(patch.desiredOutcome && { desiredOutcome: patch.desiredOutcome }),
        ...(patch.beneficiary && { beneficiary: patch.beneficiary }),
        ...(patch.evidenceOfSuccess && { evidenceOfSuccess: patch.evidenceOfSuccess }),
        ...(patch.whyNow && { whyNow: patch.whyNow }),
        ...(patch.obstacles && { obstacles: patch.obstacles }),
        ...(patch.work_type && { work_type: patch.work_type }),
        ...(patch.launch_type && { launch_type: patch.launch_type }),
        ...(patch.investment_type && { investment_type: patch.investment_type }),
        ...(patch.exit_status && { exit_status: patch.exit_status }),
        ...(patch.parentItemId && { parentItemId: patch.parentItemId }),
        ...(patch.external_id && { external_id: patch.external_id }),
        ...(patch.metadata && { metadata: patch.metadata }),
        // createdBy is intentionally omitted — createItem() stamps it from
        // the active user internally (see itemsStore.ts). Same established
        // cast used at the same call shape in semanticConnectors/
        // applySemanticImport.ts and excursionApply.ts.
      } as Parameters<typeof store.createItem>[0]);
      if (!createResult.ok) {
        failed += 1;
        failures.push({ rowNumber, title, reason: createResult.reason ?? 'The item could not be saved' });
        continue;
      }
      created += 1;
      createdItemIds.push(newId);
      anchorEntityId ??= newId;
      if (patch.external_id) {
        identityRecords.push({
          internalId: newId,
          externalId: patch.external_id,
          label: title,
        });
      }
      if (workspaceId) {
        await logChangeEvent({
          workspaceId,
          entityType: 'item',
          entityId: newId,
          eventType: 'created',
          newValue: { from: 'csv_import', externalId: patch.external_id ?? null, title },
        });
      }
      continue;
    }

    if (result.disposition === 'update' && result.matchItemId) {
      const patch = rowToItemPatch(result.row);
      // Partial PATCH: only write keys present on the row
      const updates: Partial<Item> = { ...patch };
      store.updateItem(result.matchItemId, updates);
      updated += 1;
      anchorEntityId ??= result.matchItemId;
      if (result.externalId) {
        identityRecords.push({
          internalId: result.matchItemId,
          externalId: result.externalId,
          label: String(result.row.title ?? ''),
        });
      }
      if (workspaceId) {
        await logChangeEvent({
          workspaceId,
          entityType: 'item',
          entityId: result.matchItemId,
          eventType: 'updated',
          fieldName: result.changedFields?.join(',') || undefined,
          newValue: {
            from: 'csv_import',
            externalId: result.externalId ?? null,
            changedFields: result.changedFields ?? [],
          },
        });
      }
    }
  }

  // Sync fabric identity (entity_external_ids). Change events are logged above
  // per row — do not also call registerImportedRecords (it would double-emit created).
  if (workspaceId) {
    for (const rec of identityRecords) {
      await registerExternalId({
        workspaceId,
        entityType: 'item',
        internalId: rec.internalId,
        connectorType: CSV_CONNECTOR_TYPE,
        externalId: rec.externalId,
      });
    }
  }

  if (workspaceId) {
    await logCsvRunSummary({
      workspaceId,
      summary,
      profileName: options?.profileName,
      anchorEntityId: anchorEntityId ?? identityRecords[0]?.internalId,
    });
  }

  const parts = [
    created ? `${created} created` : null,
    updated ? `${updated} updated` : null,
    unchanged ? `${unchanged} unchanged` : null,
    describeSkippedAsImported(skippedAsImported) || null,
    clarified ? `${clarified} need clarification` : null,
    failed ? `${failed} failed` : null,
  ].filter(Boolean);

  return {
    created,
    updated,
    unchanged,
    clarified,
    failed,
    failures,
    createdItemIds,
    skippedAsImported,
    importBatchId,
    summaryLabel: parts.length ? parts.join(', ') : 'No rows applied',
  };
}
